<template>
  <div class="main_contract_manager card">
    <div class="content" v-if="contract">
      
      <div class="contract_types_nav">
        <div class="nav_title">סוגי חוזה</div>
        <div class="nav_list">
          <div 
            v-for="type in contract.types" 
            :key="type.id" 
            class="nav_item" 
            :class="{ active: activeTypeId === type.id }"
            @click="selectContractType(type.id)"
          >
            <div class="nav_item_content" >
              <span class="nav_item_text">{{ type.name }}</span>
            </div>
            <button 
              v-if="contract.types.length > 1" 
              class="delete_btn" 
              @click.stop="confirmDeleteType(type)"
              title="מחק סוג חוזה"
            >
              ×
            </button>
          </div>
          <div class="nav_item add_type" @click="showAddTypeModal = true">
            <span class="nav_item_icon">+</span>
            <span class="nav_item_text">הוסף סוג חדש</span>
          </div>
        </div>
      </div>

      <div class="contract_content">
        <div v-if="activeTypeId && !isLoading" class="contracts_container" ref="contractsList">
          <contractEdit 
            ref="contractEditComponent"
            :edit_token="edit_token" 
            :contractType="currentContractType"
            @save="handleContractSave"
            @next="$emit('next')"
          />
        </div>
        <div v-else-if="isLoading" class="loading_container">
          <loader />
        </div>
        <div v-else class="empty_state">
          <div class="empty_message">בחר סוג חוזה מהתפריט או צור סוג חדש</div>
        </div>
      </div>

      <!-- Add Contract Type Modal -->
      <div v-if="showAddTypeModal" class="modal_overlay">
        <div class="modal_container">
          <div class="modal_header">
            <h3>הוספת סוג חוזה חדש</h3>
            <div class="close_btn" @click="showAddTypeModal = false">×</div>
          </div>
          <div class="modal_content">
            <div class="input_group">
              <label>שם סוג החוזה</label>
              <input type="text" v-model="newTypeName" placeholder="הזן שם לסוג החוזה החדש" />
            </div>
          </div>
          <div class="modal_footer">
            <button class="btn cancel" @click="showAddTypeModal = false">ביטול</button>
            <button class="btn submit" @click="addNewContractType">הוסף</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import contractEdit from './contractEdit.vue';
import Loader from '../Loader.vue';

export default {
  props: {
    edit_token: {
      type: String,
      required: true,
    }
  },
  components: { contractEdit, Loader },
  data() {
    return {
      contract: null,
      activeTypeId: null,
      isLoading: false,
      showAddTypeModal: false,
      newTypeName: '',
    }
  },
  computed: {
    currentContractType() {
      return this.contract.types.find(type => type.id === this.activeTypeId);
    }
  },
  async mounted() {
    const { data: contract } = await this.api({action: 'contracts/get_contract_by_types', data: { token: this.edit_token}});
    this.contract = contract;
    if (this.contract.types.length > 0) {
      this.selectContractType(this.contract.types[0].id);
    }
  },
  methods: {
    selectContractType(typeId) {
      if (this.activeTypeId === typeId) return;
      
      this.isLoading = true;
      this.activeTypeId = typeId;
      
      // Wait for component to load
      this.$nextTick(() => {
        // setTimeout(() => {
          this.isLoading = false;
        // }, 500);
      });
    },
    
    handleContractSave() {
      // Handle contract save from child component
      this.toast('החוזה נשמר בהצלחה');
    },
    
    async addNewContractType() {
      if (!this.newTypeName.trim()) {
        this.toast('נא להזין שם לסוג החוזה', 'error');
        return;
      }
      const { data: newType } = await this.api({action: 'contracts/add_type', data: { token: this.edit_token, name: this.newTypeName }});
      // Add new type to list
      this.contract.types.push({...newType, parts: []});
      
      // Close modal and reset form
      this.showAddTypeModal = false;
      this.newTypeName = '';
      
      // Select the new type
      this.selectContractType(newType.id);
    },
    
    confirmDeleteType(type) {
      this.confirm(`האם אתה בטוח שברצונך למחוק את סוג החוזה "${type.name}"?`, 'מחק', 'ביטול').then((result) => {
        if (result) {
          this.deleteContractType(type);
        }
      });
    },
    
    async deleteContractType(type) {
      try {
        const { data: is_deleted } = await this.api({
          action: 'contracts/delete_type',
          data: { 
            token: this.edit_token,
            type_id: type.id
          }
        });
        if (is_deleted) {
          // Remove the type from the list
          const index = this.contract.types.findIndex(t => t.id === type.id);
          if (index !== -1) {
            this.contract.types.splice(index, 1);
          }
          
          // If we deleted the active type, select another one
          if (this.activeTypeId === type.id) {
            this.activeTypeId = null;
            if (this.contract.types.length > 0) {
              this.selectContractType(this.contract.types[0].id);
            }
          }
        
          this.toast('סוג החוזה נמחק בהצלחה');
        }
      } catch (error) {
        this.toast('שגיאה במחיקת סוג החוזה', 'error');
        console.error('Failed to delete contract type:', error);
      }
    },
    
    autoSave() {
      // Call autoSave on the current contract edit component
      if (this.$refs.contractEditComponent) {
        this.$refs.contractEditComponent.autoSave();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.main_contract_manager {
  > .content {
    display: flex;
    flex-direction: column;
    gap: var(--gap_xmid);
    
    .btn_container {
      align-self: flex-end;
    }
  }
  
  .contract_types_nav {
    background: #f5f7fa;
    border-radius: var(--radius_s);
    padding: var(--gap);
    
    .nav_title {
      font-weight: bold;
      margin-bottom: var(--gap);
    }
    
    .nav_list {
      display: flex;
      flex-wrap: wrap;
      gap: var(--gap);
      
      .nav_item {
        background: white;
        border: 1px solid #e0e6ed;
        border-radius: var(--radius_s);
        padding: var(--gap_small) var(--gap);
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        align-items: center;
        gap: var(--gap_small);
        position: relative;
        
        .nav_item_content {
          display: flex;
          align-items: center;
          gap: var(--gap_small);
          flex: 1;
        }
        
        .delete_btn {
          background: none;
          border: none;
          color: #a0aec0;
          font-size: 18px;
          font-weight: bold;
          cursor: pointer;
          padding: 0 4px;
          margin-right: 4px;
          line-height: 1;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          
          &:hover {
            color: #e53e3e;
            background: rgba(229, 62, 62, 0.1);
          }
        }
        
        &:hover {
          border-color: var(--dark_blue);
        }
        
        &.active {
          background: var(--dark_blue);
          color: white;
          border-color: var(--dark_blue);
          
          .delete_btn {
            color: white;
            
            &:hover {
              background: rgba(255, 255, 255, 0.2);
            }
          }
        }
        
        &.add_type {
          background: #eaf6ff;
          border-color: #d0e8ff;
          
          .nav_item_icon {
            font-size: 16px;
            font-weight: bold;
          }
        }
      }
    }
  }
  
  .contract_content {
    min-height: 400px;
    position: relative;
  }
  
  .loading_container, .empty_state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 300px;
  }
  
  .empty_message {
    color: #8492a6;
    font-size: 16px;
  }
  
  // Modal styles
  .modal_overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
  }
  
  .modal_container {
    background: white;
    border-radius: var(--radius_s);
    width: 500px;
    max-width: 90%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    
    .modal_header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--gap);
      border-bottom: 1px solid #e0e6ed;
      
      .close_btn {
        font-size: 24px;
        cursor: pointer;
        padding: 0 10px;
        &:hover {
          color: var(--dark_blue);
        }
      }
    }
    
    .modal_content {
      padding: var(--gap);
      
      .input_group {
        display: flex;
        flex-direction: column;
        gap: var(--gap_small);
        
        input {
          padding: 8px 12px;
          border: 1px solid #cfd5db;
          border-radius: var(--radius_s);
          font-size: 14px;
        }
      }
    }
    
    .modal_footer {
      display: flex;
      justify-content: flex-end;
      gap: var(--gap);
      padding: var(--gap);
      border-top: 1px solid #e0e6ed;
      
      .btn {
        padding: 8px 16px;
        border-radius: var(--radius_s);
        cursor: pointer;
        
        &.cancel {
          background: #f2f4f6;
          color: #606f7b;
        }
        
        &.submit {
          background: var(--dark_blue);
          color: white;
        }
      }
    }
  }
}
</style> 